import { useState, useEffect } from 'react';
import axios from 'axios';
import { Megaphone, Loader2, Check, AlertTriangle, Save } from 'lucide-react';

const API = '/api';

/**
 * The Meta AD half of a social draft card (Randall, 18 Sep 2026 — recruiting films as ads).
 *
 * Shows the ad exactly as it would run — the text (caption minus hashtags, with the ad's own
 * counted Become-a-Fitter link), the headline, the CTA, the audience, the dates and the budget —
 * lets him change any term (each change voids an approval, the fingerprint covers them all),
 * and offers ONE deliberate action: "Approve and push to Meta", which builds the ad PAUSED.
 * He activates it in Ads Manager; the Hub never sets it live.
 *
 * LIGHT THEME: opaque hex colours only (pale Tailwind shades vanish on this background).
 */

const INK = '#1E1E1E';
const DIM = '#5B6470';
const AD = '#B4531F';          // the calendar's Meta-ad orange, darkened for text contrast
const WARN_BG = '#FFF1DB';
const WARN_FG = '#7A4100';
const OK_FG = '#1F6F2A';
const BAD_BG = '#FDE8E8';
const BAD_FG = '#8B1A1A';

export const AD_STATUS_LABEL = {
  draft: 'Not pushed yet',
  pushed_paused: 'At Meta — PAUSED (activate in Ads Manager)',
  paused: 'At Meta — paused',
  active: 'Running',
  ended: 'Ended',
};

const PLATFORM_LABEL = { facebook: 'Facebook', instagram: 'Instagram',
  audience_network: 'Audience Network', messenger: 'Messenger' };

// Where the ad runs, read from the draft. An ad with no placements is NOT "Facebook +
// Instagram": Meta would run it across its whole network, so the card says it is not set.
export function placementsLabel(placements) {
  const plats = (placements && placements.publisher_platforms) || [];
  if (!plats.length) return null;
  return plats.map((p) => {
    const pos = placements[`${p}_positions`] || [];
    return `${PLATFORM_LABEL[p] || p}${pos.length ? ` ${pos.join(', ').replace(/_/g, ' ')}` : ''}`;
  }).join(' · ');
}

function money(v) {
  return v === null || v === undefined ? '—' : `$${Number(v).toFixed(2)}`;
}

export default function SocialAdPanel({ draft, onChanged, busy: outerBusy }) {
  const ad = draft.ad || {};
  const meta = draft.meta || null;
  const metrics = (meta && meta.metrics) || null;
  const atMeta = ['pushed_paused', 'ad_active', 'ad_ended'].includes(draft.state);
  const [presets, setPresets] = useState([]);
  const [edit, setEdit] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    let live = true;
    axios.get(`${API}/social/drafts/ad-audiences`)
      .then((r) => { if (live) setPresets(r.data.presets || []); })
      .catch(() => { if (live) setPresets([]); });
    return () => { live = false; };
  }, []);

  const startEdit = () => setEdit({
    daily_budget_usd: ad.daily_budget_usd, start_date: ad.start_date, end_date: ad.end_date,
    audience_preset: ad.audience_preset, headline: ad.headline, cta: ad.cta, objective: ad.objective,
    special_ad_category: ad.special_ad_category || 'NONE',
  });

  const save = async () => {
    setBusy(true); setErr('');
    try {
      const body = {};
      Object.entries(edit).forEach(([k, v]) => {
        const cur = k === 'special_ad_category' ? (ad[k] || 'NONE') : ad[k];
        if (v !== cur && v !== '' && v !== null && v !== undefined) {
          body[k] = k === 'daily_budget_usd' ? Number(v) : v;
        }
      });
      if (Object.keys(body).length) {
        const r = await axios.put(`${API}/social/drafts/${draft.id}/ad`, body);
        onChanged(r.data);
      }
      setEdit(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not save the ad terms.');
    }
    setBusy(false);
  };

  const push = async () => {
    setBusy(true); setErr('');
    try {
      const r = await axios.post(`${API}/social/drafts/${draft.id}/push-to-meta`, { confirm: true });
      setConfirm(false);
      onChanged(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'The push did not complete.');
    }
    setBusy(false);
  };

  const snap = ad.audience_snapshot || {};
  const geo = ad.geo || {};
  const where = !geo.custom_locations
    ? (geo.countries || []).join(', ')
    : snap.near_fitters
      ? `${geo.custom_locations.length} fitter areas (${snap.radius_miles || 50} mi)`
      : geo.custom_locations.map((c) => `${c.label || 'area'} (${c.radius} mi)`).join('; ');
  const empChanges = ad.special_ad_category === 'EMPLOYMENT' ? (snap.employment_changes || []) : [];
  const disabled = busy || outerBusy;
  const runsOn = placementsLabel(ad.placements);

  return (
    <div data-testid={`ad-panel-${draft.id}`} className="space-y-3">
      <div className="flex items-center gap-2 text-[11px] font-sans uppercase tracking-wider" style={{ color: AD }}>
        <Megaphone size={13} strokeWidth={2} /> Meta ad · {runsOn || 'placements not set'} · {AD_STATUS_LABEL[draft.ad_status] || draft.ad_status}
      </div>

      <div className="border rounded-sm p-3 space-y-2" style={{ borderColor: '#E3D6CC' }}>
        <div className="text-[10px] font-sans uppercase tracking-wider" style={{ color: DIM }}>Ad text (primary text)</div>
        <p data-testid={`ad-text-${draft.id}`} className="text-sm font-sans whitespace-pre-wrap leading-relaxed break-words" style={{ color: INK }}>{draft.ad_text}</p>
        <div className="text-sm font-sans" style={{ color: INK }}>
          <span className="text-[10px] uppercase tracking-wider mr-2" style={{ color: DIM }}>Headline</span>{ad.headline}
        </div>
        <div className="text-xs font-sans" style={{ color: DIM }}>
          Button: <strong style={{ color: INK }}>{ad.cta}</strong> → the counted {ad.landing === 'partners' ? 'Facility Partners' : 'Become-a-Fitter'} link
          (<code>src=meta-ad-{ad.slug}</code>). Clicks are counted as clicks; an application counts as a lead.
        </div>
      </div>

      {!edit && (
        <dl data-testid={`ad-terms-${draft.id}`} className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs font-sans" style={{ color: DIM }}>
          <dt>Runs</dt><dd style={{ color: INK }}>{ad.start_date && ad.end_date ? `${ad.start_date} → ${ad.end_date}` : 'Not set — choose dates before approving'}</dd>
          <dt>Daily budget</dt><dd style={{ color: INK }}>{money(ad.daily_budget_usd)}/day</dd>
          <dt>Placements</dt>
          <dd data-testid={`ad-placements-${draft.id}`} style={{ color: runsOn ? INK : BAD_FG }}>
            {runsOn || 'Not set — Meta would run it everywhere, Audience Network included. The push refuses.'}
          </dd>
          <dt>Audience</dt><dd style={{ color: INK }}>{snap.name ? `${snap.name} · ${where}` : 'Not set — choose an audience before approving'}</dd>
          {snap.name && (<><dt>Ages (preset)</dt><dd style={{ color: INK }}>{snap.age_min}–{snap.age_max}</dd></>)}
          <dt>Objective</dt><dd style={{ color: INK }}>{ad.objective === 'leads_landing' ? 'Leads → landing page' : 'Traffic → landing page'}</dd>
          <dt>Category</dt><dd data-testid={`ad-category-${draft.id}`} style={{ color: INK }}>{ad.special_ad_category ? `${ad.special_ad_category} (Meta special ad category)` : 'None — a standard ad'}</dd>
          {empChanges.length > 0 && (<>
            <dt>Employment rules</dt>
            <dd data-testid={`ad-employment-${draft.id}`} style={{ color: INK }}>
              {empChanges.map((c) => <div key={c}>{c}</div>)}
            </dd>
          </>)}
        </dl>
      )}

      {edit && (
        <div data-testid={`ad-edit-${draft.id}`} className="grid grid-cols-2 gap-2 text-xs font-sans" style={{ color: DIM }}>
          <label>Daily budget ($)
            <input type="number" min="1" max="50" step="1" value={edit.daily_budget_usd ?? ''}
                   onChange={(e) => setEdit({ ...edit, daily_budget_usd: e.target.value })}
                   className="w-full border rounded-sm px-2 py-1" style={{ color: INK }} />
          </label>
          <label>Audience
            <select value={edit.audience_preset || ''} onChange={(e) => setEdit({ ...edit, audience_preset: e.target.value })}
                    className="w-full border rounded-sm px-2 py-1" style={{ color: INK }}>
              {presets.map((p) => (
                <option key={p.id} value={p.id} disabled={p.disabled}>{p.name}{p.disabled ? ' (needs the pixel)' : ''}</option>
              ))}
            </select>
          </label>
          <label>Start
            <input type="date" value={edit.start_date || ''} onChange={(e) => setEdit({ ...edit, start_date: e.target.value })}
                   className="w-full border rounded-sm px-2 py-1" style={{ color: INK }} />
          </label>
          <label>End
            <input type="date" value={edit.end_date || ''} onChange={(e) => setEdit({ ...edit, end_date: e.target.value })}
                   className="w-full border rounded-sm px-2 py-1" style={{ color: INK }} />
          </label>
          <label className="col-span-2">Headline
            <input type="text" maxLength={60} value={edit.headline || ''} onChange={(e) => setEdit({ ...edit, headline: e.target.value })}
                   className="w-full border rounded-sm px-2 py-1" style={{ color: INK }} />
          </label>
          <label>Button
            <select value={edit.cta || ''} onChange={(e) => setEdit({ ...edit, cta: e.target.value })}
                    className="w-full border rounded-sm px-2 py-1" style={{ color: INK }}>
              {['Learn More', 'Apply Now', 'Sign Up', 'Contact Us'].map((c) => <option key={c}>{c}</option>)}
            </select>
          </label>
          <label>Special ad category
            <select value={edit.special_ad_category || 'NONE'} onChange={(e) => setEdit({ ...edit, special_ad_category: e.target.value })}
                    className="w-full border rounded-sm px-2 py-1" style={{ color: INK }}>
              <option value="NONE">None — a standard ad</option>
              <option value="EMPLOYMENT">Employment</option>
            </select>
          </label>
          <label>Objective
            <select value={edit.objective || ''} onChange={(e) => setEdit({ ...edit, objective: e.target.value })}
                    className="w-full border rounded-sm px-2 py-1" style={{ color: INK }}>
              <option value="leads_landing">Leads → landing page</option>
              <option value="traffic">Traffic → landing page</option>
            </select>
          </label>
          <div className="col-span-2 flex gap-2 pt-1">
            <button data-testid={`ad-save-${draft.id}`} disabled={disabled} onClick={save}
                    className="flex items-center gap-1.5 text-xs font-sans uppercase tracking-wider px-3 py-1.5 rounded-sm text-white disabled:opacity-50"
                    style={{ background: AD }}>
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save terms
            </button>
            <button onClick={() => setEdit(null)} className="text-xs font-sans px-3 py-1.5" style={{ color: DIM }}>Cancel</button>
          </div>
          <p className="col-span-2 text-[11px]" style={{ color: DIM }}>
            Every term is part of what Randall approves — changing one voids an approval he gave.
          </p>
        </div>
      )}

      {draft.ad_issue && (
        <div data-testid={`ad-issue-${draft.id}`} className="rounded-sm p-3 text-xs font-sans space-y-1"
             style={{ background: BAD_BG, color: BAD_FG }}>
          <div className="flex gap-1.5 font-semibold"><AlertTriangle size={13} className="shrink-0 mt-0.5" />
            Meta: {draft.ad_issue.label} ({draft.ad_issue.status}) · seen {String(draft.ad_issue.seen_at || '').slice(0, 10)}</div>
          {(draft.ad_issue.reasons || []).map((r) => <div key={r}>{r}</div>)}
          {!(draft.ad_issue.reasons || []).length && <div>Meta gave no reason text — open the ad in Ads Manager.</div>}
        </div>
      )}

      {meta && (meta.campaign_id || meta.error) && (
        <div data-testid={`ad-meta-${draft.id}`} className="rounded-sm p-3 text-xs font-sans space-y-1"
             style={meta.error ? { background: BAD_BG, color: BAD_FG } : { background: '#F2F7F2', color: INK }}>
          {meta.error && <div className="flex gap-1.5"><AlertTriangle size={13} className="shrink-0 mt-0.5" />{meta.error}</div>}
          {meta.campaign_id && (
            <>
              <div>Campaign <code>{meta.campaign_id}</code> · ad set <code>{meta.adset_id}</code> · ad <code>{meta.ad_id}</code></div>
              {meta.optimisation_note && <div style={{ color: DIM }}>{meta.optimisation_note}</div>}
              {(meta.targeting_notes || []).map((n) => <div key={n} style={{ color: WARN_FG }}>{n}</div>)}
              {metrics && (
                <div data-testid={`ad-metrics-${draft.id}`}>
                  Spend <strong>{money(metrics.spend)}</strong> · {metrics.impressions} impressions ·
                  {' '}{metrics.link_clicks} link clicks at Meta
                  {metrics.cost_per_click != null && <> ({money(metrics.cost_per_click)} per click)</>}
                  {metrics.landing_clicks != null && <> · {metrics.landing_clicks} landing clicks counted in the flight</>}
                  <span style={{ color: DIM }}> · synced {String(metrics.synced_at || '').slice(0, 10)}</span>
                  <div data-testid={`ad-applications-${draft.id}`}>
                    {metrics.applications === undefined
                      ? <span style={{ color: DIM }}>Applications: not counted yet — waiting for the next sync</span>
                      : metrics.applications === null
                        ? <span style={{ color: WARN_FG }}>Applications: cannot be tied to this ad</span>
                        : metrics.applications > 0
                          ? <>Applications <strong>{metrics.applications}</strong> · {money(metrics.cpl)} per application</>
                          : <strong style={{ color: BAD_FG }}>Applications 0 — no applications yet for {money(metrics.spend)} spent</strong>}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {err && (
        <div data-testid={`ad-error-${draft.id}`} className="rounded-sm p-2 text-xs font-sans" style={{ background: BAD_BG, color: BAD_FG }}>{err}</div>
      )}

      {!atMeta && (
        <div className="flex gap-2 flex-wrap items-center">
          {!edit && (
            <button data-testid={`ad-edit-open-${draft.id}`} disabled={disabled} onClick={startEdit}
                    className="text-xs font-sans uppercase tracking-wider px-3 py-1.5 rounded-sm border disabled:opacity-50"
                    style={{ color: INK, borderColor: '#C9CED6' }}>
              Edit ad terms
            </button>
          )}
          {draft.can_push && !confirm && (
            <button data-testid={`ad-push-${draft.id}`} disabled={disabled} onClick={() => setConfirm(true)}
                    className="flex items-center gap-1.5 text-xs font-sans uppercase tracking-wider px-4 py-2 rounded-sm text-white disabled:opacity-50"
                    style={{ background: AD }}>
              <Megaphone size={13} strokeWidth={2} /> Approve and push to Meta
            </button>
          )}
          {!draft.can_push && draft.push_block && (
            <span data-testid={`ad-push-block-${draft.id}`} className="text-xs font-sans px-2 py-1 rounded-sm"
                  style={{ background: WARN_BG, color: WARN_FG }}>{draft.push_block}</span>
          )}
        </div>
      )}

      {confirm && (
        <div data-testid={`ad-confirm-${draft.id}`} className="rounded-sm p-3 space-y-2 border" style={{ borderColor: AD }}>
          <p className="text-xs font-sans" style={{ color: INK }}>
            Approve this ad and build it at Meta — <strong>PAUSED</strong>. {money(ad.daily_budget_usd)}/day,
            {' '}{ad.start_date} → {ad.end_date}, {snap.name}. Nothing spends until you activate it in Ads Manager.
          </p>
          <div className="flex gap-2">
            <button data-testid={`ad-confirm-go-${draft.id}`} disabled={disabled} onClick={push}
                    className="flex items-center gap-1.5 text-xs font-sans uppercase tracking-wider px-4 py-2 rounded-sm text-white disabled:opacity-50"
                    style={{ background: AD }}>
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Approve and push (paused)
            </button>
            <button onClick={() => setConfirm(false)} className="text-xs font-sans px-3" style={{ color: DIM }}>Cancel</button>
          </div>
        </div>
      )}

      {atMeta && (
        <p className="text-xs font-sans" style={{ color: OK_FG }}>
          At Meta. Activate, pause or change it in Ads Manager — the Hub reads its status, spend and clicks daily.
        </p>
      )}
    </div>
  );
}
